import config from 'constants/config'
import { LESCAPES_SIGNUP_DOMAIN } from 'constants/auth'
import type { ObjectOptions } from 'api/requestUtils'
import request, { authOptions } from 'api/requestUtils'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import { trackClientEvent } from 'analytics/analytics'
import { baseContext } from 'analytics/snowplow/events'
import { reportClientError } from 'services/errorReportingService'
import { getMemberSubscription } from './luxPlus'
import type { AppleLoginData, FacebookLoginData, GoogleLoginData } from 'actions/AuthActions'
import { sanitisePhoneNumber } from 'lib/format/formatPhoneNumber'

/**
 * Safely sanitizes a phone number for API calls.
 * Removes non-digits and strips leading zero (national trunk code).
 */
function getSanitisedPhone(phone?: string): string | undefined {
  return phone ? sanitisePhoneNumber(phone) : undefined
}

interface PostAuthResponse {
  access_token: string
  id_member: string
  is_sign_up: boolean
  is_new_to_brand: boolean
}

function mapAccount(serverAccount: AccountResponseResult, luxPlusMemberSubscription: App.MemberSubscription): App.AuthAccount {
  return {
    creditsCollection: { initial: true },
    balance: 0,
    memberId: serverAccount.memberId,
    referralProgramEnabled: serverAccount.referral_program_enabled,
    givenName: serverAccount.givenName,
    surname: serverAccount.surname,
    title: serverAccount.title as App.Title ?? undefined,
    email: serverAccount.email,
    dob: serverAccount.dob,
    status: serverAccount.status,
    roles: serverAccount.roles,
    vendors: serverAccount.vendors,
    postcode: serverAccount.postcode,
    phonePrefix: serverAccount.phone_prefix,
    phone: serverAccount.phone,
    partnerships: serverAccount.partnerships ?? {},
    toggles: serverAccount.toggles ?? {},
    lastLogin: serverAccount.lastLogin ? {
      ipAddress: serverAccount.lastLogin.ip_address,
      loginDate: serverAccount.lastLogin.login_date,
    } : {},
    isSpoofed: !!serverAccount.isSpoofed,
    spoofedBy: serverAccount.spoofed_by,
    fullName: serverAccount.fullName,
    legacyId: serverAccount.legacy_id ?? undefined,
    legacyPlatform: serverAccount.legacy_platform ?? undefined,
    countryCode: serverAccount.country_code,
    signupDomain: serverAccount.signup_domain,
    personContactId: serverAccount.person_contact_id,
    tenant: serverAccount.tenant,
    numberOfPurchases: serverAccount.number_of_purchases,
    customerSupportCode: serverAccount.customer_support_code,
    recentlyUsedAirportCode: serverAccount.recently_used_airport_code ?? undefined,
    language: serverAccount.language,
    emailVerified: !!serverAccount.email_verified,
    shadowBanUser: serverAccount.shadow_ban_user,
    utmSource: serverAccount.utm_source,
    utmMedium: serverAccount.utm_medium,
    utmCampaign: serverAccount.utm_campaign,
    utmTerm: serverAccount.utm_term,
    utmContent: serverAccount.utm_content,
    utmAdgroup: serverAccount.utm_adgroup,
    creditsByCurrency: {},
    subscriberTier: serverAccount.subscriber_tier,
    // @ts-expect-error `add_phone_prompted_at` doesn't exist in the contract!
    addPhonePromptedAt: serverAccount.add_phone_prompted_at,
    luxPlus: {
      member: {
        /** This is used so spoofed customers can be toggled between buying as a member or as a non member */
        disableMembership: false,
        subscription: luxPlusMemberSubscription,
      },
    },
  }
}

function parseTrackAuthResponse(res: any) {
  const idMember = res?.result?.id_member
  const status = res?.status
  return { idMember, status }
}

async function authPost(
  path: string,
  data: any,
  options: ObjectOptions = {},
  trackingProvider: App.AuthSource,
  regionCode?: string,
  luxPlusEnabled?: boolean,
) {
  const res = await request.post<App.ApiResponse<PostAuthResponse>, any>(path, data, options)

  try {
    const { idMember, status } = parseTrackAuthResponse(res)

    if (path === '/register' || res.result?.is_sign_up) {
      trackClientEvent({
        action: `${idMember}_${status}_succeeded`,
        subject: `${trackingProvider}_registration`,
        category: 'logging',
        type: 'operational',
      })
    } else {
      trackClientEvent({
        action: `${idMember}_${status}_succeeded`,
        subject: `${trackingProvider}_auth`,
        category: 'logging',
        type: 'operational',
      })
    }
  } catch(e) {
    reportClientError(e)
  }

  const accessToken = res.result.access_token
  const isSignUp = !!res.result.is_sign_up || !!res.result.is_new_to_brand
  const isSignIn = !isSignUp && path !== '/register'
  const accountResponse = await me(accessToken, regionCode, luxPlusEnabled)

  return {
    accessToken,
    account: accountResponse,
    isSignUp: path === '/register' || isSignUp,
    isSignIn,
  }
}

interface AccountResponseResult {
  givenName: string
  title: App.Title | null
  surname: string
  email: string
  fullName: string
  dob: string
  id_member: string
  memberId: string
  legacy_id: string | null
  legacy_platform: string | null
  status: string
  email_verified: boolean
  roles: Array<string>
  vendors: Array<string>
  country_code: string
  postcode: string
  phone_prefix: string
  phone: string
  signup_domain: string
  customer_support_code: string
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_content: string
  utm_term: string
  utm_adgroup: string
  updated_at: string
  qff: string
  qff_last_name: string
  partnerships: App.PartnershipsMap
  gdpr: string
  created_date: string
  recently_used_airport_code: string | null
  language?: string | null
  person_contact_id: string
  flights_enabled: boolean
  toggles: Record<string, boolean | string | null>
  referral_program_enabled: boolean
  lastLogin: {
    ip_address: string
    login_date: string
  }
  tenant: string
  number_of_purchases: number
  vip_client: boolean
  le_platinum: boolean | null
  subscriber_tier: 'base' | 'free_preview' | null
  maskedPhone: string
  isSpoofed?: boolean
  spoofed_by?: {
    id?: string
    email?: string
  }
  shadow_ban_user?: boolean
}

export function me(accessToken?: string, regionCode?: string, luxPlusEnabled?: boolean) {
  let options: ObjectOptions = { credentials: 'include' }
  if (accessToken) {
    options = { headers: { authorization: `Bearer ${accessToken}` } }
  }

  return request.get<App.ApiResponse<AccountResponseResult>>('/me', options).then(async response => {
    const luxPlusMemberSubscription: App.MemberSubscription = {
      item: undefined,
      error: undefined,
      fetching: false,
    }
    if (luxPlusEnabled && regionCode && response.result.memberId) {
      try {
        const { item, lastFreePreviews } = await getMemberSubscription({
          customerId: response.result.memberId,
          regionCode,
          accessToken,
        })
        luxPlusMemberSubscription.item = item
        luxPlusMemberSubscription.lastFreePreviews = lastFreePreviews
      }
      catch(e) {
        luxPlusMemberSubscription.error = e
      }
    }
    return mapAccount(response.result, luxPlusMemberSubscription)
  })
}

interface CheckEmailPayloadStructure {
  email?: string
  phone_prefix?: string
  phone?: string
  recaptchaResponse: string
}

interface CheckUserResult {
  isExist: boolean
  maskedPhone?: string
}

export async function checkUserExists(user: { email?: string, phoneNumber?: App.PhoneNumber }, recaptchaData?: string | null) {
  return request.post<CheckUserResult, CheckEmailPayloadStructure>(
    '/api/users/exists',
    {
      email: user.email,
      // prefer email if it's provided
      phone: user.email ? undefined : getSanitisedPhone(user.phoneNumber?.phone),
      phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
      recaptchaResponse: recaptchaData ?? '',
    },
  )
}

export interface AuthAccountLoginResponse {
  accessToken: string
  account: App.AuthAccount
}

export function login(
  user: App.User,
  password: string,
  recaptchaData?: string,
  regionCode?: string,
  luxPlusEnabled?: boolean,
): Promise<AuthAccountLoginResponse> {
  return authPost(
    '/login/recaptcha',
    {
      login: user.email,
      phone: user.email ? undefined : getSanitisedPhone(user.phoneNumber?.phone),
      phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
      password,
      recaptchaResponse: recaptchaData,
    },
    { credentials: 'include' },
    'email',
    regionCode,
    luxPlusEnabled,
  )
}

export function loginFacebook(data: FacebookLoginData, regionCode: string, luxPlusEnabled?: boolean) {
  return authPost(
    '/login/facebook',
    {
      ...data,
      signup_domain: LESCAPES_SIGNUP_DOMAIN,
      country_code: regionCode,
    },
    { credentials: 'include' },
    'facebook',
    regionCode,
    luxPlusEnabled,
  )
}

export function loginGoogle(data: GoogleLoginData, regionCode: string, luxPlusEnabled?: boolean) {
  return authPost(
    '/login/google',
    {
      ...data,
      signup_domain: LESCAPES_SIGNUP_DOMAIN,
      country_code: regionCode,
    },
    { credentials: 'include' },
    'google',
    regionCode,
    luxPlusEnabled,
  )
}

export function loginApple(data: AppleLoginData, regionCode: string, luxPlusEnabled?: boolean) {
  return authPost(
    '/login/apple',
    {
      ...data,
      signup_domain: LESCAPES_SIGNUP_DOMAIN,
      country_code: regionCode,
    },
    { credentials: 'include' },
    'apple',
    regionCode,
    luxPlusEnabled,
  )
}

export function logout() {
  return request.post('/logout', {}, { credentials: 'include' })
}

export function getSingleUseToken() {
  return request.post<any, any>('/api/single-use-token', {}, { credentials: 'include' })
}

export interface AuthAccountRegistrationResponse {
  accessToken: string
  account: App.AuthAccount
}

interface RegisterData extends Partial<App.AuthAccount> {
  password?: string
  sendWelcomeSetPasswordEmail?: boolean
  utmAdgroup?: string
  utmCampaign?: string
  utmContent?: string
  utmMedium?: string
  utmSource?: string
  utmTerm?: string
}

interface PasswordRegisterData extends RegisterData {
  password: string
}

export function register(data: RegisterData, regionCode: string, includeCredentials = true): Promise<AuthAccountRegistrationResponse> {
  const { email, password, surname, givenName, title, sendWelcomeSetPasswordEmail, utmSource, utmMedium, utmCampaign, utmTerm, utmAdgroup, utmContent, phone, phonePrefix, postcode } = data
  const signUpDomain = config.BRAND === 'luxuryescapes' ? LESCAPES_SIGNUP_DOMAIN : null
  const payload = {
    email,
    surname,
    givenName,
    title,
    ...(password !== undefined && { password }),
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_adgroup: utmAdgroup,
    utm_content: utmContent,
    country_code: regionCode,
    ...(typeof sendWelcomeSetPasswordEmail === 'boolean' && { send_welcome_set_password_email: sendWelcomeSetPasswordEmail }),
    ...(signUpDomain && { signup_domain: signUpDomain }),
    ...(phone && {
      phone_prefix: phonePrefix,
      phone: getSanitisedPhone(phone),
    }),
    postcode,
  }
  return authPost(
    '/register',
    payload,
    includeCredentials ? { credentials: 'include' } : undefined,
    'email',
    regionCode,
  )
}

// OTP Sign Up Flow Types
interface SignUpOTPPhoneNumber {
  phone: string
  prefix: string
}

interface SendSignUpOTPCodeRequest {
  phone_number: SignUpOTPPhoneNumber
}

interface VerifySignUpOTPCodeRequest {
  phone_number: SignUpOTPPhoneNumber
  code: string
}

interface SendSignUpOTPCodeResponse {
  status: number
  message: string
}

interface VerifySignUpOTPCodeResponse {
  status: number
  message: string
}

export function sendSignUpOTPCode(phoneNumber: App.PhoneNumber): Promise<SendSignUpOTPCodeResponse> {
  return request.post<SendSignUpOTPCodeResponse, SendSignUpOTPCodeRequest>(
    '/api/auth/otp/public/send-confirmation-code',
    {
      phone_number: {
        phone: getSanitisedPhone(phoneNumber.phone) ?? '',
        prefix: phoneNumber.prefix,
      },
    },
  )
}

export function verifySignUpOTPCode(phoneNumber: App.PhoneNumber, code: string): Promise<VerifySignUpOTPCodeResponse> {
  return request.post<VerifySignUpOTPCodeResponse, VerifySignUpOTPCodeRequest>(
    '/api/auth/otp/public/validate-confirmation-code',
    {
      phone_number: {
        phone: getSanitisedPhone(phoneNumber.phone) ?? '',
        prefix: phoneNumber.prefix,
      },
      code,
    },
  )
}

export function registerWithOTP(data: PasswordRegisterData, regionCode: string, includeCredentials = true): Promise<AuthAccountRegistrationResponse> {
  const { email, password, surname, givenName, sendWelcomeSetPasswordEmail, utmSource, utmMedium, utmCampaign, utmTerm, utmAdgroup, utmContent, phone, phonePrefix, postcode } = data
  const signUpDomain = config.BRAND === 'luxuryescapes' ? LESCAPES_SIGNUP_DOMAIN : null
  const payload = {
    email,
    password,
    surname,
    givenName,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_adgroup: utmAdgroup,
    utm_content: utmContent,
    country_code: regionCode,
    ...(typeof sendWelcomeSetPasswordEmail === 'boolean' && { send_welcome_set_password_email: sendWelcomeSetPasswordEmail }),
    ...(signUpDomain && { signup_domain: signUpDomain }),
    ...(phone && {
      phone_prefix: phonePrefix,
      phone: getSanitisedPhone(phone),
    }),
    postcode,
  }
  return authPost(
    '/register/otp',
    payload,
    includeCredentials ? { credentials: 'include' } : undefined,
    'email',
    regionCode,
  )
}

interface ValidateRegistrationResponse {
  status: number
  message: string
}

export function validateRegistration(data: PasswordRegisterData, regionCode: string): Promise<ValidateRegistrationResponse> {
  const { email, password, surname, givenName, phone, phonePrefix, postcode } = data
  const payload = {
    email,
    password,
    surname,
    givenName,
    country_code: regionCode,
    ...(phone && {
      phone_prefix: phonePrefix,
      phone: getSanitisedPhone(phone),
    }),
    postcode,
  }
  return request.post<ValidateRegistrationResponse, typeof payload>(
    '/register/validate',
    payload,
  )
}

interface VerifyUserEmailParams {
  brand: string
  token: string
}

interface VerifyUserEmailResponse {
  status: number
  message: string
  reset_password_link?: string
}

export async function requestUserEmailValidation({ brand, token }: VerifyUserEmailParams): Promise<VerifyUserEmailResponse> {
  return request.post<VerifyUserEmailResponse, Record<string, string>>('/api/auth/verify-user-email', { brand, token })
}

export function sendResetPasswordEmail(email: string, callbackPath?: string) {
  return request.post('/api/reset-password-email', { email, callbackPath }, { credentials: 'include' })
}

interface ResetPasswordParams {
  token: string
  password: string
  luxPlusEnabled?: boolean
  regionCode?: string
}

export function resetPassword({ token, password, luxPlusEnabled, regionCode }: ResetPasswordParams) {
  return request
    .put<App.ApiResponse<{ access_token: string }>, any>('/api/reset-token', { token, password }, { credentials: 'include' })
    .then(async response => ({
      accessToken: response.result.access_token,
      account: await me(response.result.access_token, regionCode, luxPlusEnabled),
    }))
}

function mapUserDetails(serverAccount: AccountResponseResult): Partial<App.AuthAccount> {
  return {
    givenName: serverAccount.givenName,
    surname: serverAccount.surname,
    title: serverAccount.title ?? undefined,
    email: serverAccount.email,
    dob: serverAccount.dob,
    postcode: serverAccount.postcode,
    phonePrefix: serverAccount.phone_prefix,
    phone: serverAccount.phone,
    countryCode: serverAccount.country_code,
    recentlyUsedAirportCode: serverAccount.recently_used_airport_code ?? undefined,
  }
}

export function updateUserDetails(
  account: Partial<App.AuthAccount>,
  supportPhoneNumber: string | null = null,
) {
  const serverAccount = {
    givenName: account.givenName ?? undefined,
    surname: account.surname ?? undefined,
    title: account.title ?? undefined,
    dob: account.dob ?? undefined,
    country_code: account.countryCode ?? undefined,
    phone_prefix: account.phonePrefix ?? undefined,
    phone: getSanitisedPhone(account.phone) ?? undefined,
    recently_used_airport_code: account.recentlyUsedAirportCode ?? undefined,
    postcode: account.postcode ?? undefined,
    support_phone_number: supportPhoneNumber ?? undefined,
  }

  return request.put<App.ApiResponse<AccountResponseResult>, unknown>('/api/users/current', serverAccount, { credentials: 'include' })
    .then(response => mapUserDetails(response.result))
}

interface CustomerLanguagePreferenceResponse {
  key: 'language'
  value: string
  updated_at: string
}

interface UpdateCustomerLanguagePreferenceRequest {
  value: string
}

export function updateCustomerLanguagePreferenceFromAuth(language: string, signal?: AbortSignal, accessToken?: string) {
  return request.put<App.ApiResponse<CustomerLanguagePreferenceResponse>, UpdateCustomerLanguagePreferenceRequest>(
    '/api/customers/v1/me/properties/language',
    { value: language },
    {
      ...authOptions(accessToken),
      excludeBrand: true,
      keepalive: true,
      ...(signal ? { signal } : {}),
    },
  )
}

export async function getLanguagePreferenceFromAuth(signal?: AbortSignal): Promise<string | null> {
  try {
    const response = await request.get<App.ApiResponse<CustomerLanguagePreferenceResponse>>(
      '/api/customers/v1/me/properties/language',
      { credentials: 'include', excludeBrand: true, signal },
    )
    if (signal?.aborted) return null

    return response.result.value
  } catch {
    return null
  }
}

export async function updateAddPhonePromptedAt(addPhonePromptedAt: Date) {
  const response = await request.post<App.ApiResponse<string>, unknown>('/api/users/toggles',
    {
      toggle_name: 'add_phone_prompted_at',
      toggle_value: addPhonePromptedAt.toISOString(),
    }, { credentials: 'include' })
  return response.result
}

export function updatePartnershipDetails(partnershipDetails: App.PartnershipsMap) {
  return request.put('/api/partnerships/current', partnershipDetails, { credentials: 'include' })
}

export function resendEmailConfirmation() {
  return request.get('/api/resend-email-confirmation', {
    credentials: 'include',
  })
}

const DO_NATIVE_AUTHENTICATE = 'doNativeAuthenticate'
export async function webViewAccountAccess(regionCode: string, luxPlusEnabled: boolean) {
  const authPromise = new Promise<string>((resolve, reject) => {
    window.userAuthenticated = (token: string) => {
      if (token) {
        resolve(token)
      } else {
        reject('Authentication cancelled')
      }
    }

    // Must remove region because mobile client already sets it
    const analyticsContext = baseContext().filter(c => {
      return !c.schema.includes('region')
    })
    appViewMessageHandler('authenticateUser', DO_NATIVE_AUTHENTICATE, analyticsContext)
  })
  const accessToken = await authPromise

  return {
    accessToken,
    account: await me(accessToken, regionCode, luxPlusEnabled),
  }
}

export interface ItemDetails {
  check_in?: string // Standard hotel reservations
  start_date?: string // Tours v1
  type?: string
  journey?: { id: string } // Flights
  ticket?: { date?: string } // Experiences
  departure_date?: string // Cruises
  session_id?: string // Bedbank
  departure_id?: string // Tours v2
}
export interface CheckRequireVerificationRequest {
  brand: string
  regionCode: string
  currencyCode: string
  payments: {
    payments: Array<{ type: string }>
  }
  items: Array<ItemDetails>
}

export interface CheckRequireVerificationResponse {
  verificationRequired: boolean
}

export function checkRequireVerification(checkRequireVerificationRequest: CheckRequireVerificationRequest): Promise<CheckRequireVerificationResponse> {
  // Not all order creation need otp, use this to check if otp header is required for order creation.
  const { brand, regionCode, currencyCode, payments, items } = checkRequireVerificationRequest
  return request.post('/api/orders/require_verification', {
    brand,
    region_code: regionCode,
    payments,
    items,
    currency_code: currencyCode,
  }, { credentials: 'include' })
}

export function sendSmsCode(): Promise<void> {
  return request.post<void, any>('/api/auth/otp/send-confirmation-code', {}, { credentials: 'include' })
    .catch((error) => {
      error.code = 'send_otp_code'
      throw error
    })
}

export interface VerifySmsCodeResponse {
  message: string
  token: string
  accessToken: string
}

const DEFAULT_TOKEN_EXPIRES = 'Wed, 11 Jul 2040 00:00:00 GMT'

/**
 * Persists the JWT access token to cookies
 */
export function setAuthTokenToCookies(accessToken: string): void {
  document.cookie = `ssr_access_token=${accessToken}; expires=${DEFAULT_TOKEN_EXPIRES}; path=/; domain=${document.domain}`
  document.cookie = `access_token=${accessToken}; expires=${DEFAULT_TOKEN_EXPIRES}; path=/; domain=${config.API_HOST}`
}

export async function verifySmsCode(code: string): Promise<VerifySmsCodeResponse> {
  // Use to check if OTP is expired or invalid
  const response: VerifySmsCodeResponse = await request.post('/api/auth/otp/verify-user-confirmation-code', { code }, { credentials: 'include' })
    .then(res => (res as VerifySmsCodeResponse))

  if (response.accessToken) {
    setAuthTokenToCookies(response.accessToken)
  }
  return response
}

export function sendOTPVerificationCode(user: App.User) {
  return request.post('/login/send-sms-otp', {
    email: user.email,
    phone: user.email ? undefined : getSanitisedPhone(user.phoneNumber?.phone),
    phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
    brand: config.BRAND,
  }, { credentials: 'include' })
}

export function validateOTPVerificationCode(user: App.User, code: string, regionCode?: string, luxPlusEnabled?: boolean) {
  return authPost(
    '/login/login-sms-otp',
    {
      email: user.email,
      phone: user.email ? undefined : getSanitisedPhone(user.phoneNumber?.phone),
      phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
      code,
      brand: config.BRAND,
    },
    { credentials: 'include' },
    'otp',
    regionCode,
    luxPlusEnabled,
  )
}

interface PhoneNumberResponse {
  phone: string | null
  prefix: string | null
}

export function resendEmailChangeConfirmationCode(email: string, password?: string): Promise<{ status: number, message: string, error: any }> {
  return request.post<{ status: number, message: string, error: string }, { email: string, password?: string }>('/api/auth/email-change/resend-email-change-confirmation-code', { email, password }, { credentials: 'include' })
}

export function validateEmailChangeConfirmationPhoneCode(code: string, email: string): Promise<{ status: number, message: string, error: any }> {
  return request.post<{ status: number, message: string, error: string }, { code: string, email: string }>('/api/auth/email-change/validate-email-change-confirmation-phone', { code, email }, { credentials: 'include' })
}

export function checkEmailChangePossibility(): Promise<{ email_changeable: boolean, step: string, new_email: string, error?: any }> {
  return request.post<{ step: string, status: number, email_changeable: boolean, new_email: string, error?: any }, {}>('/api/auth/email-change/initialize', {}, { credentials: 'include' })
}

export function sendEmailChangeConfirmationCode(email: string, password?: string): Promise<{ status: number, message: string, error: any }> {
  return request.post<{ status: number, message: string, error: string }, { email: string, password?: string }>('/api/auth/email-change/send-email-change-confirmation-code', { email, password }, { credentials: 'include' })
}

export function validateEmailChangeConfirmationCode(code: string, email: string, password?: string, recaptchaResponse?: string): Promise<{ status?: number, message?: string, loginData?: any, result?: any, account?: any }> {
  return request.post<{ status?: number, message?: string, loginData?: any, result?: any, account?: App.User }, { code: string, email: string, password?: string, recaptchaResponse?: string }>('/api/auth/email-change/validate-email-change-confirmation', { code, email, password, recaptchaResponse }, { credentials: 'include' })
}

export function initializePhoneChange(): Promise<{ step: string, error: string, phone_number: PhoneNumberResponse }> {
  return request.post<{ step: string, error: string, phone_number: PhoneNumberResponse }, {}>('/api/auth/phone-change/initialize', {}, { credentials: 'include' })
}

export function sendPhoneChangeConfirmationCode(phoneNumber: App.PhoneNumber, password?: string, resend?: boolean): Promise<{ status: number, message: string, error: any }> {
  const sanitisedPhoneNumber = { ...phoneNumber, phone: getSanitisedPhone(phoneNumber.phone) ?? '' }
  return request.post<{ status: number, message: string, error: string }, { phone_number: App.PhoneNumber, password?: string, resend?: boolean }>('/api/auth/phone-change/send-confirmation-code', { phone_number: sanitisedPhoneNumber, password, resend }, { credentials: 'include' })
}

export function validatePhoneChangeConfirmationCode(code: string, phoneNumber: App.PhoneNumber): Promise<{ status?: number, message?: string, phone_number: App.PhoneNumber }> {
  const sanitisedPhoneNumber = { ...phoneNumber, phone: getSanitisedPhone(phoneNumber.phone) ?? '' }
  return request.post<{ status: number, message: string, phone_number: App.PhoneNumber }, { code: string, phone_number: App.PhoneNumber }>('/api/auth/phone-change/validate-confirmation-code', { code, phone_number: sanitisedPhoneNumber }, { credentials: 'include' })
}

/**
 * Response type for similar accounts endpoint from API
 */
export interface SimilarAccountApiResponse {
  email: string
}

interface SimilarAccountsResult {
  similarAccounts: Array<SimilarAccountApiResponse>
}

/**
 * Fetch accounts with similar phone numbers (separate endpoint for privacy).
 * Only returns masked email - no IDs exposed.
 */
export function getSimilarAccounts(): Promise<Array<SimilarAccountApiResponse>> {
  return request.get<App.ApiResponse<SimilarAccountsResult>>('/me/similar-accounts', { credentials: 'include' })
    .then(response => response.result.similarAccounts)
}
